/**
 * Transit Graph: reachability cache + multi-hop pathfinding for bot jumps.
 *
 * Lazy evaluation: only computes reachability from planets as BFS explores them.
 * For a typical bot turn, this means 1-3 planets instead of all 20.
 *
 * Per-round caching: the graph persists across bot turns within the same round
 * (planets don't move). Call `invalidateTransitCache()` on turn advance.
 */

import type { PlanetState, ReachabilityEntry, TransitGraph, Vec3 } from '../types';
import { vecAdd, vecSub, vecScale, vecNormalize, vecLength, vecCross, vecDot } from '../math';
import { computeLaunchDirection } from '../core/gameState';
import { rk4Step, simulateJumpTrajectory } from '../core/physics';
import { JUMP_VELOCITY_PER_MOVEMENT, PHYS_DT, OUT_OF_BOUNDS } from '../constants';
import { isWasmReady, wasmSimulateTrajectoryHitBatch } from './wasmPhysics';

/**
 * Compute which planets are reachable by ballistic jump from a given planet.
 * Sweeps elevation × power grid for each target, recording best params.
 */
export function computeReachability(
  fromPlanetIndex: number,
  planets: PlanetState[],
  movementBudget: number,
  fuelBonus: number = 0
): ReachabilityEntry[] {
  const fromPlanet = planets[fromPlanetIndex];
  if (!fromPlanet || !fromPlanet.alive || fromPlanet.type !== 'planet') return [];

  const results: ReachabilityEntry[] = [];

  // Sweep parameters — moderate resolution for reachability estimation.
  // 6 azimuths × 5 elevations × 6 powers = 180 combos per target.
  // Sphere-only collision in WASM keeps this fast (~100x faster than heightmap).
  // The comprehensive sweep in computeBotJump (3700 sims) handles precise targeting.
  const azimuths = [-0.5, -0.2, 0, 0.2, 0.5, 0.8];
  const elevations = [0.2, 0.5, 0.7, 0.9, 1.2];
  const powers = [30, 50, 65, 78, 90, 98]; // % of movement budget
  const jumpDt = PHYS_DT; // match actual airborne flight physics

  // Build target list — sorted by distance, capped to nearest 8.
  // With 20 planets this cuts pairs from 380 to 160, and far planets are
  // unreachable by ballistic jump anyway (multi-hop covers them via BFS).
  const MAX_TARGETS = 8;
  const maxVelocity = movementBudget * JUMP_VELOCITY_PER_MOVEMENT + fuelBonus;
  // Rough max range: velocity × flight time (1200 steps × dt) × 0.6 (gravity losses)
  const maxRange = maxVelocity * (1200 * PHYS_DT) * 0.6;

  const candidates: Array<{ ti: number; dist: number }> = [];
  for (let ti = 0; ti < planets.length; ti++) {
    if (ti === fromPlanetIndex) continue;
    const target = planets[ti];
    if (!target.alive || target.type !== 'planet') continue;
    const dist = vecLength(vecSub(target.position, fromPlanet.position));
    if (dist > maxRange) continue; // unreachable by any trajectory
    candidates.push({ ti, dist });
  }
  candidates.sort((a, b) => a.dist - b.dist);
  const targetIndices = candidates.slice(0, MAX_TARGETS).map(c => c.ti);
  if (targetIndices.length === 0) return results;

  // WASM batch path: collect ALL trajectories for ALL targets into one batch
  if (isWasmReady()) {
    const allTrajectories: Array<{ pos: Vec3; vel: Vec3 }> = [];
    const comboKeys: Array<{ ti: number; az: number; elev: number; pow: number }> = [];

    for (const ti of targetIndices) {
      const target = planets[ti];
      const toTarget = vecNormalize(vecSub(target.position, fromPlanet.position));
      const launchSurfacePos = vecAdd(fromPlanet.position, vecScale(toTarget, fromPlanet.radius));
      const launchPos = vecAdd(launchSurfacePos, vecScale(toTarget, 0.5));
      const surfaceNormal = toTarget;

      for (const az of azimuths) {
        for (const elev of elevations) {
          for (const pow of powers) {
            const movementToUse = (pow / 100) * movementBudget;
            const velocity = movementToUse * JUMP_VELOCITY_PER_MOVEMENT + fuelBonus;
            const launchDir = computeLaunchDirection(surfaceNormal, az, elev);
            const launchVel = vecScale(launchDir, velocity);
            allTrajectories.push({ pos: launchPos, vel: launchVel });
            comboKeys.push({ ti, az, elev, pow });
          }
        }
      }
    }

    const batchResults = wasmSimulateTrajectoryHitBatch(
      allTrajectories, 1200, OUT_OF_BOUNDS, jumpDt
    );

    if (batchResults) {
      // Process batch results: find first hit for each target
      const foundTargets = new Set<number>();
      for (let i = 0; i < batchResults.length; i++) {
        const key = comboKeys[i];
        if (foundTargets.has(key.ti)) continue; // already found for this target
        if (batchResults[i].planetIndex === key.ti) {
          foundTargets.add(key.ti);
          results.push({
            targetPlanetIndex: key.ti,
            azimuth: key.az,
            elevation: key.elev,
            power: key.pow,
          });
        }
      }
      return results;
    }
    // WASM batch failed — fall through to JS path
  }

  // JS fallback path
  for (const ti of targetIndices) {
    const target = planets[ti];

    const toTarget = vecNormalize(vecSub(target.position, fromPlanet.position));
    const launchSurfacePos = vecAdd(fromPlanet.position, vecScale(toTarget, fromPlanet.radius));
    const launchPos = vecAdd(launchSurfacePos, vecScale(toTarget, 0.5));
    const surfaceNormal = toTarget;

    let found = false;
    let bestAz = 0;
    let bestElev = 0;
    let bestPow = 0;

    for (const az of azimuths) {
      if (found) break;
      for (const elev of elevations) {
        if (found) break;
        for (const pow of powers) {
          const movementToUse = (pow / 100) * movementBudget;
          const velocity = movementToUse * JUMP_VELOCITY_PER_MOVEMENT + fuelBonus;
          const launchDir = computeLaunchDirection(surfaceNormal, az, elev);
          const launchVel = vecScale(launchDir, velocity);

          const sim = simulateJumpTrajectory(launchPos, launchVel, planets, 1200, jumpDt);
          if (sim.landingPlanetIndex === ti) {
            bestAz = az;
            bestElev = elev;
            bestPow = pow;
            found = true;
            break;
          }
        }
      }
    }

    if (found) {
      results.push({
        targetPlanetIndex: ti,
        azimuth: bestAz,
        elevation: bestElev,
        power: bestPow,
      });
    }
  }

  return results;
}

/**
 * Build a transit graph seeded from a single planet.
 * Only computes reachability for the source; BFS lazily expands as needed.
 */
export function buildTransitGraph(
  planets: PlanetState[],
  movementBudget: number,
  fuelBonus: number = 0,
  sourcePlanetIndex?: number,
): TransitGraph {
  const graph: TransitGraph = new Map();

  if (sourcePlanetIndex !== undefined) {
    // Lazy mode: only compute for the source planet up front.
    // findTransitPath will expand neighbors on demand.
    graph.set(sourcePlanetIndex, computeReachability(sourcePlanetIndex, planets, movementBudget, fuelBonus));
  } else {
    // Full mode (legacy fallback): compute for all planets
    for (let i = 0; i < planets.length; i++) {
      const p = planets[i];
      if (!p.alive || p.type !== 'planet') continue;
      graph.set(i, computeReachability(i, planets, movementBudget, fuelBonus));
    }
  }

  return graph;
}

/**
 * BFS pathfinding over the transit graph with lazy expansion.
 * When a node hasn't been computed yet, computes its reachability on demand.
 * Returns ordered planet indices [from, intermediate..., to], or null if unreachable.
 * Max BFS depth of 3 hops to bound computation.
 */
export function findTransitPath(
  graph: TransitGraph,
  fromPlanet: number,
  toPlanet: number,
  planets?: PlanetState[],
  movementBudget?: number,
  fuelBonus?: number,
  maxHops: number = 3,
): number[] | null {
  if (fromPlanet === toPlanet) return [fromPlanet];

  // Check direct reachability first
  const directEntries = graph.get(fromPlanet);
  if (directEntries?.some(e => e.targetPlanetIndex === toPlanet)) {
    return [fromPlanet, toPlanet];
  }

  // BFS with lazy expansion — hop limit bounds the cost (and, per bot, how far it will route: BotTuning.jumping.maxHops)
  const MAX_HOPS = Math.max(1, Math.floor(maxHops));
  const visited = new Set<number>([fromPlanet]);
  const queue: Array<{ node: number; path: number[] }> = [
    { node: fromPlanet, path: [fromPlanet] },
  ];

  while (queue.length > 0) {
    const current = queue.shift()!;
    if (current.path.length > MAX_HOPS) continue; // depth limit

    // Lazy expansion: compute reachability if not yet in graph
    if (!graph.has(current.node) && planets && movementBudget !== undefined) {
      graph.set(current.node, computeReachability(current.node, planets, movementBudget, fuelBonus ?? 0));
    }

    const neighbors = graph.get(current.node) ?? [];

    for (const entry of neighbors) {
      const next = entry.targetPlanetIndex;
      if (visited.has(next)) continue;
      visited.add(next);

      const newPath = [...current.path, next];
      if (next === toPlanet) return newPath;
      queue.push({ node: next, path: newPath });
    }
  }

  return null; // unreachable
}

// =============================================================================
// Per-round transit graph cache (Option C)
// =============================================================================

// R9: one graph per (movement budget, fuel bonus) within a round — reachability depends on both
const cachedGraphs = new Map<string, TransitGraph>();
let cachedRoundKey = '';

/**
 * Get or build a transit graph, caching per round.
 * The cache key includes planet positions hash + movement budget.
 * Planets don't move within a round, so the graph is reusable across bot turns.
 */
export function getOrBuildTransitGraph(
  planets: PlanetState[],
  movementBudget: number,
  fuelBonus: number,
  sourcePlanetIndex: number,
  roundKey: string,
): TransitGraph {
  // If the round changed, clear cache
  if (roundKey !== cachedRoundKey) {
    cachedGraphs.clear();
    cachedRoundKey = roundKey;
  }

  const key = `${movementBudget}:${fuelBonus}`;
  let graph = cachedGraphs.get(key);
  if (!graph) { graph = new Map(); cachedGraphs.set(key, graph); }

  // Compute reachability for this source if not cached
  if (!graph.has(sourcePlanetIndex)) {
    graph.set(sourcePlanetIndex, computeReachability(sourcePlanetIndex, planets, movementBudget, fuelBonus));
  }

  return graph;
}

/** Clear the transit graph cache (call on round/turn advance or game reset). */
export function invalidateTransitCache(): void {
  cachedGraphs.clear();
  cachedRoundKey = '';
}
